import { Component, inject, signal, OnInit, OnDestroy, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, of, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, DynamicFilterConfig, DynamicFilterValues, FilterOption, createDynamicFilterState } from '@card-software/library';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DepartmentOption, SupplyRequest, SupplyRequestSearchCommand } from '../../../models/supply-request/supply-request';
import { SupplyRequestProxyService } from '../../../proxies/inventory/supply-request.proxy.service';
import { DepartmentProxyService } from '../../../proxies/user-master';
import { Department } from '../../../models/user-master/department';
import { EnumService } from '../../../services/enum.service';
import { LocalHelperService } from '../../../services/local-helper.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-supply-request-list',
  templateUrl: './supply-request-list.component.html',
  styleUrl: './supply-request-list.component.scss',
})
export class SupplyRequestListComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private supplyRequestProxyService = inject(SupplyRequestProxyService);
  private departmentProxyService = inject(DepartmentProxyService);
  private userAuthenticationService = inject(UserAuthenticationService);
  private translateService = inject(TranslateService);
  public enumService = inject(EnumService);
  public localHelperService = inject(LocalHelperService);

  private destroy$ = new Subject<void>();

  isLoading = signal(0);
  showFilters = signal<boolean>(true);
  dataSource = signal<SupplyRequest[]>([]);
  canCreate = computed(() => AuthService.hasRole(UserGroup.SupplyRequestAdmin));

  statuses = this.enumService.getEnum('SupplyRequestStatus', 'inventory').values;
  statusOptions = computed<FilterOption[]>(() =>
    Object.keys(this.statuses)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({ value: Number(key), label: this.statuses[Number(key)] })),
  );

  departments = signal<DepartmentOption[]>([]);
  filteredDepartments = signal<DepartmentOption[]>([]);

  // Dynamic filters
  filtersConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'supply-request-filters', config: this.filtersConfig });

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));
  }

  ngOnInit(): void {
    this.loadDepartments();

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.initializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDepartments(): void {
    this.departmentProxyService.getAll().subscribe({
      next: (response) => {
        const options: DepartmentOption[] = (response.data ?? []).map((dep: Department) => ({
          id: dep.id,
          name: this.localHelperService.getLocalizedValue(dep.name),
        }));
        this.departments.set(options);
        this.filteredDepartments.set(options);
        this.initializeFilterConfig();
      },
      error: () => {
        this.departments.set([]);
        this.filteredDepartments.set([]);
        this.initializeFilterConfig();
      },
    });
  }

  filterDepartments(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    this.filteredDepartments.set(this.departments().filter((d) => d.name?.toLowerCase().includes(query)));
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };
    const filteredDepartments = this.filteredDepartments;

    this.filtersConfig.set({
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('SupplyRequest_Number', 'Number'),
          placeholder: translate('Enter_supply_request_number', 'Enter supply request number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'departmentReference',
          label: translate('SupplyRequest_Department', 'Department'),
          placeholder: translate('Select_department', 'Select department'),
          autocompleteConfig: {
            get suggestions() {
              return filteredDepartments();
            },
            optionLabel: 'name',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterDepartments(event),
            resolveById: (id: string) => of(this.departments().find((department) => department.id === id) ?? null),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('SupplyRequest_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: this.statusOptions(),
          showClear: true,
          defaultVisible: false,
        },
      ],
    });
  }

  //#REGION Dynamic Filter Section
  private searchWithFilters(filters: DynamicFilterValues): void {
    const department = filters['departmentReference'] as string | { id?: string } | null | undefined;
    const departmentId = typeof department === 'string' ? department : department?.id;

    const statusesRaw = filters['statuses'];
    const statuses: number[] = Array.isArray(statusesRaw)
      ? statusesRaw.map((s: { value?: number }) => (typeof s === 'object' && s.value !== undefined ? s.value : s) as number)
      : [];

    const command: SupplyRequestSearchCommand = {
      number: (filters['number'] as string | undefined)?.trim() || undefined,
      createdBy: this.userAuthenticationService.userLite?.username,
      departmentReference: departmentId || undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
    };

    this.loadTableData(command);
  }

  //#ENDREGION

  startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  stopLoading(): void {
    this.isLoading.update((val) => Math.max(0, val - 1));
  }

  getStrictSupplyRequest(supplyRequest: unknown): SupplyRequest {
    return supplyRequest as SupplyRequest;
  }

  loadTableData(command?: SupplyRequestSearchCommand) {
    const searchCommand = command ?? { createdBy: this.userAuthenticationService.userLite?.username };

    this.startLoading();
    this.supplyRequestProxyService
      .search(searchCommand)
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe({
        next: (response) => {
          this.dataSource.set(response.data ?? []);
        },
      });
  }

  redirectToDetail(id?: string): void {
    const queryParams = id ? { id: id } : {};
    this.router.navigate(['/supply-request-detail'], { queryParams });
  }
}
